import { CreditCard, LogOut, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { authClient } from "@/lib/auth-client";
import { queryClient } from "@/utils/trpc";

export function HeaderUser({
  user,
  showSubscriptionLink = false,
}: {
  user: {
    name: string;
    planName?: string | null;
    avatar?: string;
    initials: string;
  };
  showSubscriptionLink?: boolean;
}) {
  const navigate = useNavigate();

  const handleSignOut = async () => {
    queryClient.clear();
    try {
      await authClient.signOut();
    } catch (error) {
      console.error("Sign out error:", error);
    } finally {
      navigate("/");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2.5 rounded-full outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/45"
          aria-label="User menu"
        >
          <Avatar className="h-10 w-10 rounded-full">
            <AvatarImage src={user.avatar} alt={user.name} />
            <AvatarFallback className="rounded-full bg-foreground text-background text-xs font-bold">
              {user.initials}
            </AvatarFallback>
          </Avatar>
          <div className="hidden text-left leading-tight md:block">
            <div className="text-sm font-medium truncate max-w-[14rem]">
              {user.name}
            </div>
            {user.planName ? (
              <div className="text-xs uppercase tracking-wide text-muted-foreground truncate">
                {user.planName}
              </div>
            ) : null}
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="min-w-56 rounded-lg"
        side="bottom"
        align="end"
        sideOffset={8}
      >
        {showSubscriptionLink ? (
          <DropdownMenuItem onSelect={() => navigate("/billing")}>
            <CreditCard />
            Billing
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onSelect={() => navigate("/account")}>
          <Settings />
          Account
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void handleSignOut()}>
          <LogOut />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
